import { AppContextService } from '../../../services/app-context.service';
import { Optional, ElementRef } from '@angular/core';
import { viewContextVisible } from '../../../shared/view-context';
import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { BrIconComponent } from '../../../shared/ui/icon/icon.component';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { DataListComponent } from '../../../shared/ui/data-list/data-list.component';
import { DataListItemDirective } from '../../../shared/ui/data-list/data-list-item.directive';
import { GridCell } from '../../../map/core/grid-object-selection';
import { LeadFeature } from '../../../map/features/leads/lead-feature';
import { ProspectFeature } from '../../../map/features/prospects/prospect-feature';
import { DataFetch } from '../../../models/data-source';
import { CacheService } from '../../../services/cache.service';
import { isPullActive, PlacePullService, PullCell, PullPlace, PullSummary } from '../../../services/place-pull.service';

/** One history item owns its content view and lazy results. The parent polls job summaries. */
@Component({
  selector: 'app-pull-job-item', standalone: true,
  imports: [CommonModule, BrIconComponent, DataListComponent, DataListItemDirective],
  templateUrl: './pull-job-item.component.html', styleUrls: ['./pull-job-item.component.css'],
})
export class PullJobItemComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) job!: PullSummary;
  @Input() initiallyExpanded = false;
  @ViewChild('resultsList') resultsList?: DataListComponent<PullPlace>;
  @ViewChild('cellsList') cellsList?: DataListComponent<PullCell>;
  expanded = false;
  view: 'results' | 'grids' | 'details' = 'results';
  canceling = false;
  actionError = '';
  private cancelAccepted = false;
  private resultsDirty = false;
  private readonly closed$ = new Subject<void>();
  private readonly destroyed$ = new Subject<void>();
  private leadSub?: Subscription;
  private contextRegistration?: Subscription;
  readonly active = isPullActive;
  readonly trackPlace = (_index: number, row: PullPlace): string => row.cellIndex + ':' + row.placeId;
  readonly trackCell = (_index: number, row: PullCell): number => row.cellIndex;
  readonly fetchPlaces: DataFetch<PullPlace> = query => this.pull.places(this.job.jobId, query.page, query.pageSize).pipe(
    takeUntil(this.closed$), takeUntil(this.cache.sessionChanged$), takeUntil(this.destroyed$),
  );
  readonly fetchCells: DataFetch<PullCell> = query => this.pull.cells(this.job.jobId, query.page, query.pageSize).pipe(
    takeUntil(this.closed$), takeUntil(this.cache.sessionChanged$), takeUntil(this.destroyed$),
  );

  constructor(private readonly pull: PlacePullService, private readonly feature: ProspectFeature,
    private readonly leads: LeadFeature, private readonly cache: CacheService,
    @Optional() private readonly context?: AppContextService,
    @Optional() private readonly element?: ElementRef<HTMLElement>) {}

  get cancelRequested(): boolean { return this.job.cancelRequested || this.cancelAccepted; }

  ngOnInit(): void {
    this.expanded = this.initiallyExpanded;
    this.contextRegistration = this.context?.registerView({
      key: `discovery-job:${this.job.jobId}`, name: 'Discovery task', priority: 5,
      active: () => this.expanded && viewContextVisible(this.element),
      read: () => ({ jobId: this.job.jobId, expanded: this.expanded, view: this.view }),
      describe: state => JSON.stringify({ ...state, status: this.job.status }),
      restore: state => {
        if (!state.expanded) this.collapse();
        else { this.expanded = true; this.view = state.view; }
      },
    });
    this.cache.sessionChanged$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.collapse(); this.canceling = false; this.cancelAccepted = false; this.actionError = '';
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const previous = changes['job']?.previousValue as PullSummary | undefined;
    if (!previous) return;
    if (!isPullActive(this.job)) this.cancelAccepted = false;
    if (this.expanded && (previous.cellsDone !== this.job.cellsDone || previous.status !== this.job.status || this.resultsDirty)) {
      this.refreshResults();
    }
  }

  toggle(): void {
    if (this.expanded) this.collapse();
    else { this.expanded = true; this.resultsDirty = false; }
  }

  private collapse(): void {
    this.closed$.next();
    this.leadSub?.unsubscribe();
    this.expanded = false; this.view = 'results';
  }

  refreshResults(): void {
    this.resultsDirty = true;
    // Keep the final checkpoint pending if a result page is still in flight.
    // The next history update retries even when the job is already terminal.
    if (!this.expanded || this.resultsList?.loading || this.cellsList?.loading) return;
    this.resultsList?.refresh();
    this.cellsList?.refresh();
    this.resultsDirty = false;
  }

  focusGrid(cell: GridCell): void {
    this.feature.focusObject({ id: cell.zoom + ':' + cell.tileX + ':' + cell.tileY,
      zoom: cell.zoom, tileX: cell.tileX, tileY: cell.tileY });
  }

  focusLead(row: PullPlace): void {
    if (row.leadId == null) return;
    this.actionError = '';
    this.leadSub?.unsubscribe();
    this.leadSub = this.leads.focusById(row.leadId).pipe(
      takeUntil(this.closed$), takeUntil(this.cache.sessionChanged$), takeUntil(this.destroyed$),
    ).subscribe({ error: () => this.actionError = 'This Lead could not be located. It may have been archived, merged or no longer have map coordinates.' });
  }

  cancel(): void {
    if (!isPullActive(this.job) || this.cancelRequested || this.canceling) return;
    this.canceling = true; this.actionError = '';
    // Collapsing the item must not abort a cancellation the user already requested.
    this.pull.cancel(this.job.jobId).pipe(takeUntil(this.cache.sessionChanged$), takeUntil(this.destroyed$)).subscribe({
      next: () => { this.cancelAccepted = true; this.canceling = false; },
      error: () => { this.actionError = 'Unable to cancel. Please try again.'; this.canceling = false; },
    });
  }

  ngOnDestroy(): void {
    this.contextRegistration?.unsubscribe();
    this.destroyed$.next(); this.destroyed$.complete(); this.closed$.complete();
  }
}
